import {
  LayoutDashboard,
  MessageSquare,
  CalendarDays,
  Bot,
  CalendarClock,
  Building2,
  Users,
  Sparkles,
  Wrench,
  AlertTriangle,
  Brain,
  BookOpen,
  Settings,
  BarChart3,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

// App-level roles (matches app_role DB enum)
export type AppRole = 'admin' | 'property_manager' | 'maintenance' | 'cleaner';

// i18n keys for role labels
export const APP_ROLE_LABELS: Record<AppRole, string> = {
  admin: 'roles.admin',
  property_manager: 'roles.propertyManager',
  maintenance: 'roles.maintenance',
  cleaner: 'roles.cleaner',
};

export const APP_ROLE_COLORS: Record<AppRole, { color: string; bgColor: string }> = {
  admin: { color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900' },
  property_manager: { color: 'text-indigo-600', bgColor: 'bg-indigo-100 dark:bg-indigo-900' },
  maintenance: { color: 'text-orange-600', bgColor: 'bg-orange-100 dark:bg-orange-900' },
  cleaner: { color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900' },
};

// All staff roles (for the outer route guard)
export const ALL_STAFF_ROLES: AppRole[] = ['admin', 'property_manager', 'maintenance', 'cleaner'];

// Per-path access matrix
const ROLE_PAGES: Record<AppRole, string[]> = {
  admin: ['*'],
  property_manager: ['/properties', '/profile'],
  maintenance: ['/operations', '/properties', '/ops/maintenance', '/profile'],
  cleaner: ['/operations', '/properties', '/ops/cleaning', '/profile'],
};

export function canAccessPath(role: AppRole | null, path: string): boolean {
  if (!role) return false;
  if (role === 'admin') return true;
  const allowed = ROLE_PAGES[role] || [];
  return allowed.some(p => path === p || path.startsWith(p + '/'));
}

// Navigation item definition
export interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  roles: AppRole[];
  section?: 'main' | 'ops' | 'ai';
}

// Centralized navigation items (single source of truth for Header + Sidebar)
export const NAV_ITEMS: NavItem[] = [
  // Main section
  { path: '/dashboard', label: 'navigation.dashboard', icon: LayoutDashboard, roles: ['admin'], section: 'main' },
  { path: '/messages', label: 'navigation.messages', icon: MessageSquare, roles: ['admin'], section: 'main' },
  { path: '/bookings', label: 'navigation.bookings', icon: CalendarDays, roles: ['admin'], section: 'main' },
  // { path: '/stay-agent', label: 'navigation.stayAgent', icon: Bot, roles: ['admin'], section: 'main' }, // Hidden - accessible via Messages
  { path: '/operations', label: 'navigation.operations', icon: CalendarClock, roles: ['admin', 'maintenance', 'cleaner'], section: 'ops' },
  { path: '/properties', label: 'navigation.properties', icon: Building2, roles: ['admin', 'property_manager', 'maintenance', 'cleaner'], section: 'main' },
  { path: '/team', label: 'navigation.team', icon: Users, roles: ['admin'], section: 'main' },

  // Ops section
  { path: '/ops/cleaning', label: 'navigation.cleaning', icon: Sparkles, roles: ['admin', 'cleaner'], section: 'ops' },
  { path: '/ops/maintenance', label: 'navigation.maintenance', icon: Wrench, roles: ['admin', 'maintenance'], section: 'ops' },

  // AI section
  { path: '/ai-analytics', label: 'navigation.aiAnalytics', icon: Sparkles, roles: ['admin'], section: 'ai' },
  { path: '/escalations', label: 'navigation.escalations', icon: AlertTriangle, roles: ['admin'], section: 'ai' },
  { path: '/knowledge', label: 'navigation.knowledge', icon: Brain, roles: ['admin'], section: 'ai' },
  { path: '/knowledge-review', label: 'navigation.knowledgeReview', icon: BookOpen, roles: ['admin'], section: 'ai' },
  { path: '/admin/rlhf', label: 'navigation.rlhf', icon: Settings, roles: ['admin'], section: 'ai' },
  { path: '/analytics', label: 'navigation.analytics', icon: BarChart3, roles: ['admin'], section: 'ai' },
];

/** Filter nav items by user role */
export function getNavItemsForRole(role: AppRole | null): NavItem[] {
  if (!role) return [];
  if (role === 'admin') return NAV_ITEMS;
  return NAV_ITEMS.filter(item => item.roles.includes(role));
}

/** Get the default landing page for a role */
export function getDefaultPageForRole(role: AppRole): string {
  switch (role) {
    case 'admin': return '/dashboard';
    case 'property_manager': return '/properties';
    case 'maintenance': return '/ops/maintenance';
    case 'cleaner': return '/ops/cleaning';
    default: return '/profile';
  }
}
